import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import moment from 'moment';

import {
  Datalist,
  Button,
} from '@ktag/styleguide/dist/react';

import {
  PaymentStore,
  FormConfigStore,
  ArticlesStore,
  MunicipalityStore,
  ArticleInfo,
  DeliveryInfoSummary,
  TypeCreateOrderFN,
  DateFormatter,
  NewPaymentAndDelivery,
  PaymentAndDelivery,
} from 'ewp-common';

import orderProcess from '../../../services/orderProcess';
import formDataStore from '../../../stores/formDataStore';

interface Props {
  id?: string;
  className?: string;
  additionalClassNames?: string;
  setRoute?: Dispatch<SetStateAction<number>>;
  createOrderFn:TypeCreateOrderFN
}

const Payment = (props: Props) => {
  const {
    id,
    className,
    additionalClassNames,
    setRoute,
    createOrderFn,
  } = props;

  const {
    state: articlesState,
    fetchArticles,
    totalAmount,
  } = useContext(ArticlesStore.ArticlesContext);

  const amountCurrency = articlesState?.articles?.data?.[0]?.currency_ISO;

  const {
    state: formConfigState,
  } = useContext(FormConfigStore.FormConfigContext);

  const {
    state: formDataState,
  } = useContext(formDataStore.FormDataContext);

  const {
    state: municipalityState,
  } = useContext(MunicipalityStore.MunicipalityContext);

  const municipalityId = municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID;

  const {
    state: paymentState,
  } = useContext(PaymentStore.PaymentContext);

  const createOrder = async () => {
    const formDataValue = {
      ...formDataState,
      municipalityId: municipalityState.selectedMunicipality?.data?.gemeinde?.gemeinde_ID,
    };
    const reqData = {
      formData: formDataValue,
      amountData: {
        amount: articlesState.amount,
        currency: amountCurrency,
      },
      paymentMethod: paymentState.selectedPaymentMethod,
      paymentCollectionType: paymentState.paymentCollectionType,
      deliveryType: paymentState.deliveryType
    };
    createOrderFn(
      orderProcess.createOrder,
      reqData,
      formConfigState?.data?.labelsMap?.errorLabel,
      () => setRoute(4),
    );
  };

  const {
    personal,
    primaryAddressInfo,
    secondaryAddressInfo,
    deliveryInfo,
  } = formDataState;

  const { search } = useLocation();
  const status = search && new URLSearchParams(search).get('status');

  const data = formConfigState?.data?.labelsMap;

  const {
    fieldAnrede,
    fieldLastname,
    fieldFirstname,
    fieldAhvNumber,
    fieldBirthDate,
    fieldPhone,
    fieldEmail,
    fieldValidFrom,
    fieldEstimatedStay,
    fieldReasonForStay,
    fieldComments,
  } = personal;

  const diff = moment().diff(fieldBirthDate, 'years');
  console.log(diff);

  useEffect(() => {
    if (status?.toLowerCase() === 'success') {
      setRoute(4);
    }
    if (municipalityId) {
      fetchArticles(municipalityId);
    }
    window.scrollTo(0, 0);
  }, []);

  let isDeliveryAddressSchweiz = true;

  if(deliveryInfo?.fieldDeliveryAddressDifferent != null && ((deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' && deliveryInfo?.fieldLand != 'Schweiz') || (deliveryInfo?.fieldDeliveryAddressDifferent === 'Nein' && primaryAddressInfo.fieldLand != 'Schweiz'))){
    isDeliveryAddressSchweiz = false;
  }

  return (
    <div id={id} className={classNames(className, additionalClassNames)}>
      <h2>{data?.h2SummaryAndPayment?.labelDisplayName}</h2>
      <h3 className="mt-4">{data?.h2PersonalInfo?.labelDisplayName}</h3>
      <Datalist
        items={[
          {
            key: data.fieldAnrede?.labelDisplayName,
            value: fieldAnrede,
          },
          {
            key: data.fieldLastname.labelDisplayName,
            value: fieldLastname,
          },
          {
            key: data.fieldFirstname.labelDisplayName,
            value: fieldFirstname,
          },
          {
            key: data.fieldAhvNumber?.labelDisplayName,
            value: fieldAhvNumber,
          },
          {
            key: data.fieldBirthDate.labelDisplayName,
            value: DateFormatter.formatDateForDisplay(fieldBirthDate),
          },
          {
            key: data.fieldPhone?.labelDisplayName,
            value: fieldPhone,
          },
          {
            key: data.fieldEmail?.labelDisplayName,
            value: fieldEmail,
          },
        ]}
      />
      <h3 className="mt-4">{data.h2PrimaryResidence?.labelDisplayName}</h3>
      <Datalist items={[
        {
          key: data.fieldPrimaryStrasseNr?.labelDisplayName,
          value: `${primaryAddressInfo.fieldStrasse} ${primaryAddressInfo.fieldNr}`,
        },
        {
          key: data.fieldPrimaryPlzOrt?.labelDisplayName,
          value: `${primaryAddressInfo.fieldPlz} ${primaryAddressInfo.fieldOrt}`,
        },
        {
          key: data.fieldPrimaryLand?.labelDisplayName,
          value: primaryAddressInfo.fieldLand,
        },
      ]}
      />
      <h3 className="mt-4">{data.h2SecondaryResidence?.labelDisplayName}</h3>
      <Datalist items={[
        {
          key: data.fieldValidFrom?.labelDisplayName,
          value: DateFormatter.formatDateForDisplay(fieldValidFrom),
        },
        {
          key: data.fieldSecondaryStrasseNr?.labelDisplayName,
          value: `${secondaryAddressInfo.fieldStrasse} ${secondaryAddressInfo.fieldNr}`,
        },
        {
          key: data.fieldSecondaryPlzOrt?.labelDisplayName,
          value: `${secondaryAddressInfo.fieldPlz} ${secondaryAddressInfo.fieldOrt}`,
        },
        {
          key: data.fieldSecondaryLand?.labelDisplayName,
          value: secondaryAddressInfo.fieldLand,
        },
        {
          key: data.fieldEstimatedStay?.labelDisplayName,
          value: fieldEstimatedStay == "" ? "Unbegrenzt" : fieldEstimatedStay,
        },
        {
          key: data.fieldReasonForStay?.labelDisplayName,
          value: fieldReasonForStay,
        },
      ]}
      />
      <DeliveryInfoSummary formConfigs={data} deliveryInfoFormData={deliveryInfo} />
      <Datalist items={[
        {
          key: data.fieldComments?.labelDisplayName,
          value: fieldComments,
        },
      ]}
      />
      {diff >= 18 ?
        <ArticleInfo
          municipalityId={municipalityId}
          labelConfig={data}
          deliveryAddressCountry={deliveryInfo?.fieldDeliveryAddressDifferent != null && deliveryInfo?.fieldDeliveryAddressDifferent === 'Ja' ? deliveryInfo?.fieldLand : primaryAddressInfo?.fieldLand}
          selectedDeliveryCode={paymentState.deliveryType}
          isPersonUnderage={diff < 18 ? true : false}
        /> :
        <></>
      }

      {process.env.REACT_APP_USE_NEW_PAYMENT_AND_DELIVERY_METHOD == "1" ?
      <NewPaymentAndDelivery
        paymentTitle={data.fieldPaymentType?.labelDisplayName}
        municipalityId={municipalityId}
        labelConfig={data}
        showPaymentOption={diff < 18 ? false : true}
        showDeliveryOption={true}
        isDeliveryAddressSchweiz={isDeliveryAddressSchweiz}
      /> :
      <PaymentAndDelivery
        paymentTitle={data.fieldPaymentType?.labelDisplayName}
        municipalityId={municipalityId}
        labelConfig={data}
        showPaymentOption={true}
        showDeliveryOption={true}
      />}
      <div className="payment-actions">
        <Button
          label={data.btnCancel?.labelDisplayName}
          onClick={() => {
            setRoute(2);
            window.location.reload;
          }}
        />
        <Button
          label={data.btnCompleteOrder?.labelDisplayName}
          onClick={createOrder}
          primary
          disabled={
            (!paymentState?.selectedDeliveryMethod)
            || (paymentState.paymentMethods?.data?.length > 0 && !paymentState.selectedPaymentMethod)
            || (paymentState.selectedPaymentMethod?.value?.toLowerCase() === '3' && !paymentState.paymentCollectionType)
            || (paymentState.selectedDeliveryMethod?.deliveryCode?.toLowerCase() === '3' && !paymentState.deliveryType)
          }
        />
      </div>
    </div>
  );
};

Payment.defaultProps = {
  id: '',
  className: 'payment',
  additionalClassNames: '',
};

export default Payment;
